/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Sophia Default Icon React Component
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  SOPHIA_DEFAULT_ICONS_DATA,
  type SophiaDefaultIconName,
} from '../icons-data';

export interface SophiaIconProps extends React.SVGAttributes<SVGElement> {
  name: SophiaDefaultIconName;
  size?: number | string;
  className?: string;
  title?: string;
  ariaLabel?: string;
}

/**
 * Universal SVG Icon component for Sophia Platform default icons library.
 * Designed for edge-native, zero-bundle-IO performance across User Workspace & Admin Portal.
 */
export const SophiaIcon: React.FC<SophiaIconProps> = ({
  name,
  size = 20,
  className = '',
  title,
  ariaLabel,
  style,
  ...rest
}) => {
  const icon = SOPHIA_DEFAULT_ICONS_DATA[name];

  if (!icon) {
    const isDev =
      typeof globalThis !== 'undefined' &&
      (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process?.env?.NODE_ENV !== 'production';

    if (isDev) {
      console.warn(`[SophiaIcon] Icon "${name}" does not exist in Sophia Default Icons.`);
    }
    return null;
  }

  const dimension = typeof size === 'number' ? `${size}px` : size;

  // React does not allow both children and dangerouslySetInnerHTML simultaneously.
  // Prepend sanitized <title> directly into inner HTML when title prop is provided.
  const innerHtml = title
    ? `<title>${title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</title>${icon.innerHtml}`
    : icon.innerHtml;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={icon.viewBox || '0 0 32 32'}
      width={dimension}
      height={dimension}
      fill="currentColor"
      aria-hidden={!ariaLabel && !title}
      aria-label={ariaLabel || title}
      role={ariaLabel || title ? 'img' : 'presentation'}
      data-sophia-icon={name}
      className={className}
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        flexShrink: 0,
        ...style,
      }}
      dangerouslySetInnerHTML={{ __html: innerHtml }}
      {...rest}
    />
  );
};

export default SophiaIcon;
